import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Switch,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Container,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  fetchAIProfileById,
  generateAIProfileContent,
  createPost,
  fetchComponentCode,
  storeManualComponent,
  toggleAIProfileVisibility,
  generateAIImage,
  getAIResponse,
  updateAIProfile,
  generateAIProfileImage,
  preparePrompt,
} from "../api";
import PostCard from "./PostCard";
import { rewriteToCloudFront } from "../images";
import DynamicComponent from "./DynamicComponent";
import ProfileAvatar from "./ProfileAvatar";
import { getOrCreateUserId } from "../api";
import PostList from "./PostList";
import FollowButton from "./FollowButton";

function AIProfilePage({ isAdmin }) {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState("");
  const [generatedComponentId, setGeneratedComponentId] = useState(null);
  const [previewSource, setPreviewSource] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [manualJson, setManualJson] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [generatedJson, setGeneratedJson] = useState(null);

  useEffect(() => {
    loadProfile();
  }, [id]);

  // Add effect to set userInput when profile loads
  useEffect(() => {
    if (profile?.prompt) {
      setUserInput(profile.prompt);
    }
  }, [profile]);

  const loadProfile = async () => {
    try {
      const data = await fetchAIProfileById(id);
      // Transform each post in data.Posts
      const userId = getOrCreateUserId();
      const updatedPosts = data.Posts.map((p) => {
        const likeCount = p.Likes ? p.Likes.length : 0;
        const userHasLiked = p.Likes
          ? p.Likes.some((like) => like.userId === userId)
          : false;
        return {
          ...p,
          likeCount,
          userHasLiked,
        };
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by createdAt in descending order

      setProfile({
        ...data,
        Posts: updatedPosts,
      });
    } catch (err) {
      console.error("Failed to fetch AI profile:", err);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      // Get the prompt to use
      const promptToUse = userInput?.trim() || profile.prompt;

      // If there's a new prompt, save it
      if (userInput?.trim() && userInput.trim() !== profile.prompt) {
        try {
          const updatedProfile = await updateAIProfile(
            id,
            profile.name,
            profile.description,
            userInput.trim()
          );
          setProfile((prev) => ({
            ...prev,
            prompt: updatedProfile.prompt,
          }));
        } catch (err) {
          console.error("Failed to save new prompt:", err);
        }
      }

      // Ask the server to do CONTEXT substitution
      const finalPrompt = await preparePrompt(id, promptToUse);

      console.log("Final prompt with context:", finalPrompt);

      // Get raw response from AI
      const response = await getAIResponse(finalPrompt);
      console.log("Raw AI response:", response);
      setAiResponse(response);

      // Parse the response as JSON
      let generatedJson;
      try {
        // Only escape newlines within string literals
        const processedResponse = response.replace(
          /"([^"]*?)"/g,
          (match, p1) => {
            return `"${p1.replace(/\n/g, "\\n")}"`;
          }
        );
        generatedJson = JSON.parse(processedResponse);
        setGeneratedJson(generatedJson); // Store the parsed JSON
      } catch (err) {
        console.error("Failed to parse AI-generated JSON:", err);
        console.log("Raw text that failed to parse:", response);
        // If parsing fails, treat the text as plain text
        generatedJson = { text: response };
        setGeneratedJson(generatedJson); // Store the plain text as JSON
      }

      // Process the JSON structure
      await processGeneratedJson(generatedJson);
    } catch (err) {
      console.error("Error generating AI content:", err);
      // Try to extract and show the text from the error response if available
      if (err.response) {
        try {
          const errorData = await err.response.json();
          console.log("Error response data:", errorData);
        } catch (e) {
          console.log("Could not parse error response as JSON");
        }
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyJson = async () => {
    try {
      // Only escape newlines within string literals
      const processedJson = manualJson.replace(/"([^"]*?)"/g, (match, p1) => {
        return `"${p1.replace(/\n/g, "\\n")}"`;
      });
      const parsed = JSON.parse(processedJson);
      await processGeneratedJson(parsed);
    } catch (err) {
      console.error("Error applying user-supplied JSON:", err);
      alert(
        "Invalid JSON or error processing content. See console for details."
      );
    }
  };

  const processGeneratedJson = async (json) => {
    // Set the text content
    setGeneratedText(json.text || "");

    // Handle image generation if there's an imagePrompt
    if (json.imagePrompt) {
      try {
        const imageUrl = await generateAIImage(json.imagePrompt);
        setGeneratedImage(imageUrl);
      } catch (err) {
        console.error("Error generating image:", err);
      }
    } else {
      setGeneratedImage(null);
    }

    // Handle component creation if there's componentSource
    if (json.componentSource) {
      const newId = "cmp-" + Date.now();
      try {
        await storeManualComponent(
          newId,
          json.componentSource,
          "Component from JSON"
        );
        setGeneratedComponentId(newId);

        setIsPreviewLoading(true);
        const data = await fetchComponentCode(newId);
        setPreviewSource(data.source);
        setIsPreviewLoading(false);
      } catch (err) {
        console.error("Error storing component:", err);
      }
    } else {
      setGeneratedComponentId(null);
      setPreviewSource(null);
    }
  };

  const handlePost = async () => {
    // Actually create a new Post in the DB using the user's generated text & optional image
    if (!generatedText.trim()) return;
    try {
      // We can optionally pass an AIProfileId if we want to associate the post to this AI
      // But if you want a "user" post, you could omit it. Let's associate with the AI for demonstration.
      const newPost = await createPost(
        generatedText,
        generatedImage,
        id,
        generatedComponentId,
        generatedJson?.imagePrompt // Use the imagePrompt from the generated JSON
      );

      // Insert the new post at top of the feed with AIProfile information
      setProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          Posts: [
            {
              ...newPost,
              AIProfile: {
                id: prev.id,
                name: prev.name,
                description: prev.description,
              },
            },
            ...prev.Posts,
          ],
        };
      });

      // Reset local form states
      setGeneratedText("");
      setGeneratedImage(null);
      setGeneratedComponentId(null);
      setPreviewSource(null);
      setImagePrompt("");
    } catch (error) {
      console.error("Error creating post with AI content:", error);
    }
  };

  const handleVisibilityToggle = async () => {
    try {
      const updatedProfile = await toggleAIProfileVisibility(id);
      setProfile((prev) => ({ ...prev, isVisible: updatedProfile.isVisible }));
    } catch (err) {
      console.error("Failed to toggle visibility:", err);
    }
  };

  const handlePostUpdated = (updatedPost) => {
    setProfile((prev) => ({
      ...prev,
      Posts: prev.Posts.map((post) =>
        post.id === updatedPost.id ? updatedPost : post
      ),
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset states when switching tabs
    setGeneratedText("");
    setGeneratedImage(null);
    setGeneratedComponentId(null);
    setPreviewSource(null);
    setManualJson("");
  };

  const handleEditClick = () => {
    setEditName(profile.name);
    setEditDescription(profile.description || "");
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    try {
      const updatedProfile = await updateAIProfile(
        id,
        editName,
        editDescription
      );
      setProfile((prev) => ({
        ...prev,
        name: updatedProfile.name,
        description: updatedProfile.description,
      }));
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to update AI profile:", err);
      alert("Failed to update AI profile. Please try again.");
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setIsGeneratingImage(true);
    try {
      console.log("Generating image with prompt:", imagePrompt);
      const updatedProfile = await generateAIProfileImage(id, imagePrompt);
      console.log("Received updated profile:", updatedProfile);

      // Update the profile state with the new image URL
      setProfile((prev) => ({
        ...prev,
        imageUrl: updatedProfile.imageUrl,
      }));

      // Clear the prompt after successful generation
      setImagePrompt("");
    } catch (err) {
      console.error("Error generating profile image:", err);
      alert("Failed to generate profile image. See console for details.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  if (!profile) {
    return (
      <Typography sx={{ p: 2, color: "text.secondary" }}>Loading...</Typography>
    );
  }

  const maybeImage = generatedImage
    ? rewriteToCloudFront(generatedImage)
    : null;

  return (
    <Container maxWidth="sm">
      {isAdmin ? (
        // Admin mode - keep existing layout
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
              {profile.name}
            </Typography>
            <IconButton onClick={handleEditClick} color="primary">
              <EditIcon />
            </IconButton>
          </Box>

          {/* Profile Image Section */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <ProfileAvatar profile={profile} size={100} />
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Image Prompt"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="Describe the image you want to generate..."
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleGenerateImage}
                disabled={isGeneratingImage || !imagePrompt.trim()}
              >
                {isGeneratingImage ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generate Profile Image"
                )}
              </Button>
            </Box>
          </Box>

          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            {profile.description}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={profile.isVisible}
                onChange={handleVisibilityToggle}
                size="small"
              />
            }
            label="Visible"
          />

          {/* Tabs section - only in admin mode */}
          <Box sx={{ mt: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="AI Generation" />
              <Tab label="Manual JSON Input" />
            </Tabs>

            {activeTab === 0 && (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, mt: 2, color: "text.secondary" }}
                >
                  Generate new content using this AI profile. You can either:
                  <ul>
                    <li>Use the current prompt</li>
                    <li>Edit the prompt to customize the generation</li>
                  </ul>
                  The AI will generate a JSON structure that may include text,
                  image prompts, and component code.
                  <br />
                  <br />
                  You can use the <code>&lt;CONTEXT&gt;</code> tag in your
                  prompt to include all visible posts from this AI profile.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={50}
                  variant="outlined"
                  label="Generation Prompt"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-root": {
                      resize: "vertical",
                      overflow: "auto",
                      minHeight: "200px",
                    },
                    "& .MuiInputBase-input": {
                      maxHeight: "none !important",
                      height: "100% !important",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  startIcon={
                    isGenerating ? <CircularProgress size={20} /> : null
                  }
                  sx={{ mb: 2 }}
                >
                  {isGenerating ? "Generating..." : "Generate Content"}
                </Button>

                {/* Raw Output Section */}
                {(generatedText || generatedImage || previewSource) && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      backgroundColor: "#1e1e1e", // Dark background
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, color: "white" }}
                    >
                      Raw AI Response
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#d4d4d4", // Light gray text
                        backgroundColor: "#1e1e1e",
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: "#333",
                      }}
                    >
                      {aiResponse || "No raw response available"}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, mt: 2, color: "text.secondary" }}
                >
                  Paste a JSON object with the following structure:
                  <pre
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "1rem",
                      borderRadius: "4px",
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                      overflow: "auto",
                    }}
                  >
                    {`{
  "text": "Your post text here",
  "imagePrompt": "A beautiful sunset over mountains",  // optional, will generate an image
  "componentSource": "// Your component code here"  // optional
}`}
                  </pre>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  placeholder="Paste your JSON here..."
                  value={manualJson}
                  onChange={(e) => setManualJson(e.target.value)}
                  sx={{
                    mb: 2,
                    fontFamily: "monospace",
                    "& .MuiInputBase-root": {
                      resize: "vertical",
                      overflow: "auto",
                      minHeight: "150px",
                    },
                    "& .MuiInputBase-input": {
                      maxHeight: "none !important",
                      height: "100% !important",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleApplyJson}
                  disabled={!manualJson.trim()}
                >
                  Process JSON
                </Button>
              </>
            )}

            {/* Preview section */}
            {(generatedText || generatedImage || previewSource) && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Preview Generated Content
                </Typography>
                {generatedText && (
                  <Typography sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                    {generatedText}
                  </Typography>
                )}
                {generatedImage && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={rewriteToCloudFront(generatedImage)}
                      alt="Generated"
                      style={{ maxWidth: "100%", borderRadius: "4px" }}
                    />
                  </Box>
                )}
                {previewSource && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Dynamic Component Preview:
                    </Typography>
                    <DynamicComponent
                      source={previewSource}
                      isAdmin={isAdmin}
                    />
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePost}
                  disabled={!generatedText.trim()}
                >
                  Post Generated Content
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        // Non-admin mode - more compact layout
        <Box sx={{ mb: 2, px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            {/* avatar */}
            <Grid item sx={{ flexShrink: 0 }}>
              <ProfileAvatar profile={profile} size={{ xs: 48, sm: 64 }} />
            </Grid>

            {/* title – may wrap to two lines */}
            <Grid item xs>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.25,
                  fontSize: { xs: "1.6rem", sm: "2rem" },
                  wordBreak: "break-word",
                }}
              >
                {profile.name}
              </Typography>
            </Grid>

            {/* follow button on the same row, right‑aligned */}
            <Grid item sx={{ flexShrink: 0 }}>
              <FollowButton profileId={profile.id} />
            </Grid>

            {/* second row – tagline / description */}
            <Grid item xs={12}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: { xs: 0.5, sm: 1 }, wordBreak: "break-word" }}
              >
                {profile.description}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Edit Dialog - only shown when isEditing is true */}
      <Dialog open={isEditing} onClose={() => setIsEditing(false)}>
        <DialogTitle>Edit AI Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditing(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <PostList
        initialPosts={profile.Posts || []}
        onPostUpdated={handlePostUpdated}
        isAdmin={isAdmin}
        useInfiniteScroll={false}
      />
    </Container>
  );
}

export default AIProfilePage;
